import React, { useMemo, useState, useCallback } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  InputBase,
  MenuItem,
  Select,
  SelectChangeEvent,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import RefreshIcon from "@mui/icons-material/Refresh";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import EqualizerOutlinedIcon from "@mui/icons-material/EqualizerOutlined";
import InventoryOutlinedIcon from "@mui/icons-material/InventoryOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import HourglassTopOutlinedIcon from "@mui/icons-material/HourglassTopOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import CloseIcon from "@mui/icons-material/Close";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useTranslation } from "react-i18next";
import WhmFilterFieldsGrid from "../../../components/whm/WhmFilterFieldsGrid";
import {
  BRAND,
  CONTROL_HEIGHT,
  NJIM,
  glassCardSx,
  pageShellSx,
  searchFieldSx,
  softFieldSx,
  whmFilterHeaderSx,
  whmFilterLabelSx,
  whmFilterSelectSx,
} from "../../../theme/njimTheme";

/* ─── types ─── */
type ReconciliationStatus = "matched" | "pending" | "discrepancy";

interface ReconciliationRow {
  id: string;
  itemCode: string;
  description: string;
  b2bTotalQty: number;
  b2bPendingQty: number;
  sageStock: number;
  stockSite: string;
}

/* ─── mock data ─── */
const MOCK_DATA: ReconciliationRow[] = [
  { id: "1",  itemCode: "AH-AMOX-500",  description: "Amoxicillin 500mg Capsules",          b2bTotalQty: 1200, b2bPendingQty: 0,  sageStock: 1200, stockSite: "HAAP1" },
  { id: "2",  itemCode: "AH-PARA-1G",   description: "Paracetamol 1000mg Tablets",          b2bTotalQty: 3500, b2bPendingQty: 200, sageStock: 3300, stockSite: "HAAP1" },
  { id: "3",  itemCode: "AH-IBUP-400",  description: "Ibuprofen 400mg Tablets",             b2bTotalQty: 800,  b2bPendingQty: 0,  sageStock: 750,  stockSite: "HAAP1" },
  { id: "4",  itemCode: "AH-ATOR-20",   description: "Atorvastatin 20mg Tablets",           b2bTotalQty: 600,  b2bPendingQty: 50, sageStock: 550,  stockSite: "HAAP1" },
  { id: "5",  itemCode: "AH-METF-850",  description: "Metformin 850mg Tablets",             b2bTotalQty: 2100, b2bPendingQty: 0,  sageStock: 2100, stockSite: "HAAP2" },
  { id: "6",  itemCode: "AH-OMEP-20",   description: "Omeprazole 20mg Capsules",            b2bTotalQty: 450,  b2bPendingQty: 0,  sageStock: 500,  stockSite: "HAAP2" },
  { id: "7",  itemCode: "AH-AZIT-500",  description: "Azithromycin 500mg Tablets",          b2bTotalQty: 320,  b2bPendingQty: 80, sageStock: 240,  stockSite: "HAAP2" },
  { id: "8",  itemCode: "AH-CIPR-250",  description: "Ciprofloxacin 250mg Tablets",         b2bTotalQty: 900,  b2bPendingQty: 0,  sageStock: 900,  stockSite: "HAAP2" },
  { id: "9",  itemCode: "AH-LOSR-50",   description: "Losartan 50mg Tablets",               b2bTotalQty: 1100, b2bPendingQty: 100, sageStock: 1000, stockSite: "HAAP3" },
  { id: "10", itemCode: "AH-ALOP-300",  description: "Allopurinol 300mg Tablets",           b2bTotalQty: 700,  b2bPendingQty: 0,  sageStock: 700,  stockSite: "HAAP3" },
  { id: "11", itemCode: "AH-CLAR-500",  description: "Clarithromycin 500mg Tablets",        b2bTotalQty: 250,  b2bPendingQty: 0,  sageStock: 300,  stockSite: "HAAP3" },
  { id: "12", itemCode: "AH-PROP-40",   description: "Propranolol 40mg Tablets",            b2bTotalQty: 1800, b2bPendingQty: 150, sageStock: 1650, stockSite: "HAAP3" },
  { id: "13", itemCode: "AH-DIAZ-5",    description: "Diazepam 5mg Tablets",                b2bTotalQty: 400,  b2bPendingQty: 0,  sageStock: 380,  stockSite: "HAAP1" },
  { id: "14", itemCode: "AH-DOXY-100",  description: "Doxycycline 100mg Capsules",          b2bTotalQty: 560,  b2bPendingQty: 60, sageStock: 500,  stockSite: "HAAP2" },
  { id: "15", itemCode: "AH-CETI-10",   description: "Cetirizine 10mg Tablets",             b2bTotalQty: 2200, b2bPendingQty: 0,  sageStock: 2200, stockSite: "HAAP3" },
];

function getStatus(row: ReconciliationRow): ReconciliationStatus {
  const gap = row.b2bTotalQty - row.sageStock;
  if (gap === 0) return "matched";
  if (gap === row.b2bPendingQty) return "pending";
  return "discrepancy";
}

const thSx = {
  fontWeight: 600,
  color: NJIM.textSecondary,
  fontSize: "11px",
  textTransform: "uppercase" as const,
  letterSpacing: "0.4px",
  py: 1.2,
  borderBottom: "1px solid rgba(226,232,240,0.8)",
  background: NJIM.muted,
  whiteSpace: "nowrap" as const,
};

const tdSx = {
  fontSize: "13px",
  color: NJIM.textPrimary,
  py: 1.3,
  borderBottom: "1px solid rgba(226,232,240,0.4)",
};

const STATUS_STYLES: Record<ReconciliationStatus, { bg: string; color: string; label: string }> = {
  matched:     { bg: "rgba(22,163,74,0.08)",   color: NJIM.success, label: "Matched" },
  pending:     { bg: "rgba(245,158,11,0.1)",   color: NJIM.warning, label: "Pending" },
  discrepancy: { bg: "rgba(239,68,68,0.08)",   color: NJIM.danger, label: "Discrepancy" },
};

const ROW_BG: Record<ReconciliationStatus, string> = {
  matched:     "transparent",
  pending:     "rgba(245,158,11,0.04)",
  discrepancy: "rgba(239,68,68,0.04)",
};

/* ─── component ─── */
const InventoryReconciliation: React.FC = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language?.toLowerCase().startsWith("ar");

  const [searchText, setSearchText]         = useState("");
  const [siteFilter, setSiteFilter]         = useState("all");
  const [statusFilter, setStatusFilter]     = useState("all");
  const [loading, setLoading]               = useState(false);
  const [lastRefreshed, setLastRefreshed]   = useState<Date>(new Date());

  /* ─ derived: enrich rows with status & gap ─ */
  const enriched = useMemo(
    () => MOCK_DATA.map((r) => ({ ...r, gap: r.b2bTotalQty - r.sageStock, status: getStatus(r) })),
    []
  );

  /* ─ unique sites ─ */
  const sites = useMemo(
    () => Array.from(new Set(MOCK_DATA.map((r) => r.stockSite))).sort(),
    []
  );

  /* ─ filtered rows ─ */
  const filteredRows = useMemo(() => {
    const query = searchText.trim().toLowerCase();
    return enriched.filter((r) => {
      if (query && !r.itemCode.toLowerCase().includes(query) && !r.description.toLowerCase().includes(query)) return false;
      if (siteFilter !== "all" && r.stockSite !== siteFilter) return false;
      if (statusFilter === "discrepancy" && r.status !== "discrepancy") return false;
      if (statusFilter === "pending"     && r.status !== "pending")     return false;
      if (statusFilter === "matched"     && r.status !== "matched")     return false;
      return true;
    });
  }, [enriched, searchText, siteFilter, statusFilter]);

  /* ─ stats ─ */
  const stats = useMemo(() => ({
    total:        enriched.length,
    discrepancy:  enriched.filter((r) => r.status === "discrepancy").length,
    pendingQty:   enriched.reduce((s, r) => s + r.b2bPendingQty, 0),
    withPending:  enriched.filter((r) => r.b2bPendingQty > 0).length,
  }), [enriched]);

  const handleRefresh = useCallback(() => {
    setLoading(true);
    setTimeout(() => {
      setLastRefreshed(new Date());
      setLoading(false);
    }, 900);
  }, []);

  const clearFilters = () => {
    setSearchText("");
    setSiteFilter("all");
    setStatusFilter("all");
  };

  const hasActiveFilters = searchText || siteFilter !== "all" || statusFilter !== "all";

  const summaryCards = [
    {
      icon: <InventoryOutlinedIcon sx={{ fontSize: 20, color: NJIM.white }} />,
      gradient: `${NJIM.walletHero}`,
      value: stats.total,
      label: t("inventoryReconciliation.totalItems", { defaultValue: "Total Items" }),
      sub:   t("inventoryReconciliation.trackedProducts", { defaultValue: "Tracked products" }),
    },
    {
      icon: <WarningAmberOutlinedIcon sx={{ fontSize: 20, color: NJIM.white }} />,
      gradient: NJIM.danger,
      value: stats.discrepancy,
      label: t("inventoryReconciliation.withDiscrepancies", { defaultValue: "With Discrepancies" }),
      sub:   t("inventoryReconciliation.requiresInvestigation", { defaultValue: "Require investigation" }),
    },
    {
      icon: <HourglassTopOutlinedIcon sx={{ fontSize: 20, color: NJIM.white }} />,
      gradient: `linear-gradient(135deg, ${NJIM.warning}, ${NJIM.warning})`,
      value: stats.pendingQty.toLocaleString("en-US"),
      label: t("inventoryReconciliation.totalPendingQty", { defaultValue: "Total Pending Qty" }),
      sub:   t("inventoryReconciliation.allocatedUnposted", { defaultValue: "Allocated but unposted" }),
    },
    {
      icon: <AccessTimeOutlinedIcon sx={{ fontSize: 20, color: NJIM.white }} />,
      gradient: NJIM.info,
      value: stats.withPending,
      label: t("inventoryReconciliation.itemsWithPending", { defaultValue: "Items with Pending" }),
      sub:   t("inventoryReconciliation.pendingAllocations", { defaultValue: "Have pending allocations" }),
    },
  ];

  return (
    <Box sx={pageShellSx} dir={isRTL ? "rtl" : "ltr"}>
      <Box sx={{ maxWidth: 1400, mx: "auto", px: { xs: 2, md: 4 }, py: 3 }}>

        {/* ── Header ── */}
        <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", flexWrap: "wrap", gap: 2, mb: 3 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box sx={{ width: 44, height: 44, borderRadius: "11px", background: NJIM.walletHero, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <EqualizerOutlinedIcon sx={{ color: NJIM.white, fontSize: 22 }} />
            </Box>
            <Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography sx={{ fontSize: 20, fontWeight: 700, color: NJIM.textPrimary }}>
                  {t("inventoryReconciliation.title", { defaultValue: "Inventory Reconciliation" })}
                </Typography>
                <Chip
                  label="WHM"
                  size="small"
                  sx={{ fontWeight: 700, fontSize: 11, background: `${NJIM.walletHero}`, color: NJIM.white, height: 22, borderRadius: "6px" }}
                />
              </Box>
              <Typography sx={{ fontSize: 13, color: NJIM.textSecondary, mt: 0.2 }}>
                {t("inventoryReconciliation.subtitle", { defaultValue: "Compare B2B platform quantities against live Sage X3 stock" })}
              </Typography>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.3 }}>
                <AccessTimeOutlinedIcon sx={{ fontSize: 12, color: NJIM.textMuted }} />
                <Typography sx={{ fontSize: 11, color: NJIM.textMuted }}>
                  {t("inventoryReconciliation.lastRefreshed", { defaultValue: "Last refreshed" })}: {lastRefreshed.toLocaleTimeString()}
                </Typography>
              </Box>
            </Box>
          </Box>

          <Box sx={{ display: "flex", gap: 1.5, flexShrink: 0 }}>
            <Button
              onClick={handleRefresh}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={14} sx={{ color: BRAND }} /> : <RefreshIcon sx={{ fontSize: 16 }} />}
              sx={{
                textTransform: "none", fontSize: 13, fontWeight: 600,
                border: "1px solid rgba(22,45,58,0.25)", color: BRAND,
                borderRadius: "8px", px: 2, py: 0.7,
                "&:hover": { background: "rgba(10,61,120,0.04)" },
              }}
            >
              {loading
                ? t("inventoryReconciliation.refreshing", { defaultValue: "Refreshing…" })
                : t("inventoryReconciliation.refresh",    { defaultValue: "Refresh" })}
            </Button>
            <Button
              startIcon={<FileDownloadOutlinedIcon sx={{ fontSize: 16 }} />}
              sx={{
                textTransform: "none", fontSize: 13, fontWeight: 600,
                background: `${NJIM.walletHero}`,
                color: NJIM.white, borderRadius: "8px", px: 2, py: 0.7,
                "&:hover": { opacity: 0.9 },
              }}
            >
              {t("inventoryReconciliation.export", { defaultValue: "Export" })}
            </Button>
          </Box>
        </Box>

        {/* ── Summary Cards ── */}
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(4, 1fr)" }, gap: 2, mb: 3 }}>
          {summaryCards.map((c, i) => (
            <Box key={i} sx={{ ...glassCardSx, p: 2.5 }}>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1.5 }}>
                <Box sx={{ width: 36, height: 36, borderRadius: "9px", background: c.gradient, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  {c.icon}
                </Box>
              </Box>
              <Typography sx={{ fontSize: 26, fontWeight: 800, color: NJIM.textPrimary, lineHeight: 1.1 }}>
                {c.value}
              </Typography>
              <Typography sx={{ fontSize: 13, fontWeight: 600, color: NJIM.textPrimary, mt: 0.4 }}>{c.label}</Typography>
              <Typography sx={{ fontSize: 11, color: NJIM.textMuted, mt: 0.2 }}>{c.sub}</Typography>
            </Box>
          ))}
        </Box>

        {/* ── Filters ── */}
        <Box sx={{ ...glassCardSx, p: 2.5, mb: 2.5, overflow: "visible" }}>
          <Box sx={whmFilterHeaderSx}>
            <FilterAltOutlinedIcon sx={{ fontSize: 16, color: NJIM.textSecondary }} />
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: NJIM.textPrimary }}>
              {t("inventoryReconciliation.filters", { defaultValue: "Filters" })}
            </Typography>
            {hasActiveFilters && (
              <Box
                onClick={clearFilters}
                sx={{ marginInlineStart: "auto", display: "flex", alignItems: "center", gap: 0.5, cursor: "pointer", color: NJIM.textSecondary, "&:hover": { color: NJIM.danger }, fontSize: 12, fontWeight: 600 }}
              >
                <CloseIcon sx={{ fontSize: 13 }} />
                {t("inventoryReconciliation.clearFilters", { defaultValue: "Clear All Filters" })}
              </Box>
            )}
          </Box>
          <WhmFilterFieldsGrid>
            {/* Search */}
            <Box>
              <Typography sx={whmFilterLabelSx}>
                {t("inventoryReconciliation.search", { defaultValue: "Item Code / Description" })}
              </Typography>
              <Box
                component="form"
                onSubmit={(e) => e.preventDefault()}
                sx={{ ...searchFieldSx, ...softFieldSx, px: 1.2, height: CONTROL_HEIGHT, boxSizing: "border-box" }}
              >
                <SearchIcon sx={{ color: NJIM.textMuted, fontSize: 16 }} />
                <InputBase
                  placeholder={t("inventoryReconciliation.searchPlaceholder", { defaultValue: "Search by code or name…" })}
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  sx={{ fontSize: 13, flex: 1 }}
                />
              </Box>
            </Box>

            {/* Site */}
            <Box>
              <Typography sx={whmFilterLabelSx}>
                {t("inventoryReconciliation.site", { defaultValue: "Site" })}
              </Typography>
              <Select
                fullWidth size="small" value={siteFilter}
                onChange={(e: SelectChangeEvent) => setSiteFilter(e.target.value)}
                sx={whmFilterSelectSx}
              >
                <MenuItem value="all">{t("inventoryReconciliation.allSites", { defaultValue: "All Sites" })}</MenuItem>
                {sites.map((s) => <MenuItem key={s} value={s} sx={{ fontSize: 13 }}>{s}</MenuItem>)}
              </Select>
            </Box>

            {/* Status */}
            <Box>
              <Typography sx={whmFilterLabelSx}>
                {t("inventoryReconciliation.statusFilter", { defaultValue: "Status" })}
              </Typography>
              <Select
                fullWidth size="small" value={statusFilter}
                onChange={(e: SelectChangeEvent) => setStatusFilter(e.target.value)}
                sx={whmFilterSelectSx}
              >
                <MenuItem value="all">{t("inventoryReconciliation.allItems",     { defaultValue: "All Items" })}</MenuItem>
                <MenuItem value="discrepancy">{t("inventoryReconciliation.discrepanciesOnly", { defaultValue: "Discrepancies Only" })}</MenuItem>
                <MenuItem value="pending">{t("inventoryReconciliation.withPendingQty",  { defaultValue: "With Pending Qty" })}</MenuItem>
                <MenuItem value="matched">{t("inventoryReconciliation.matchedOnly",     { defaultValue: "Matched Only" })}</MenuItem>
              </Select>
            </Box>
          </WhmFilterFieldsGrid>
        </Box>

        {/* ── Legend ── */}
        <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 1.5, mb: 2 }}>
          <Typography sx={{ fontSize: 12, color: NJIM.textSecondary, fontWeight: 600, mr: 0.5 }}>
            {t("inventoryReconciliation.legend", { defaultValue: "Legend:" })}
          </Typography>
          {([
            { status: "matched"     as const, icon: <CheckCircleOutlineIcon sx={{ fontSize: 13 }} /> },
            { status: "pending"     as const, icon: <AccessTimeOutlinedIcon sx={{ fontSize: 13 }} /> },
            { status: "discrepancy" as const, icon: <WarningAmberOutlinedIcon sx={{ fontSize: 13 }} /> },
          ]).map(({ status, icon }) => {
            const s = STATUS_STYLES[status];
            return (
              <Box key={status} sx={{ display: "flex", alignItems: "center", gap: 0.6, px: 1.2, py: 0.4, borderRadius: "6px", background: s.bg }}>
                <Box sx={{ color: s.color, display: "flex" }}>{icon}</Box>
                <Typography sx={{ fontSize: 12, fontWeight: 600, color: s.color }}>
                  {t(`inventoryReconciliation.status.${status}`, { defaultValue: s.label })}
                </Typography>
              </Box>
            );
          })}
        </Box>

        {/* ── Table ── */}
        <Box sx={{ ...glassCardSx, overflow: "hidden" }}>
          <Box sx={{ px: 3, py: 2, borderBottom: "1px solid rgba(226,232,240,0.8)", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <Box>
              <Typography sx={{ fontSize: 15, fontWeight: 700, color: NJIM.textPrimary }}>
                {t("inventoryReconciliation.tableTitle", { defaultValue: "Reconciliation Data" })}
              </Typography>
              <Typography sx={{ fontSize: 12, color: NJIM.textMuted, mt: 0.2 }}>
                {t("inventoryReconciliation.showing", { defaultValue: "Showing {{count}} of {{total}} items", count: filteredRows.length, total: enriched.length })}
              </Typography>
            </Box>
            {hasActiveFilters && (
              <Chip
                label={`${filteredRows.length} result${filteredRows.length !== 1 ? "s" : ""}`}
                size="small"
                sx={{ background: NJIM.mintWash, color: BRAND, fontWeight: 700, fontSize: 12 }}
              />
            )}
          </Box>

          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
              <CircularProgress size={32} sx={{ color: BRAND }} />
            </Box>
          ) : filteredRows.length === 0 ? (
            <Box sx={{ textAlign: "center", py: 8 }}>
              <Typography sx={{ fontSize: 14, color: NJIM.textMuted }}>
                {t("inventoryReconciliation.noResults", { defaultValue: "No items found matching your filters" })}
              </Typography>
            </Box>
          ) : (
            <Box sx={{ overflowX: "auto" }}>
              <Table sx={{ minWidth: 900 }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ ...thSx, width: 42, textAlign: "center" }}>#</TableCell>
                    <TableCell sx={thSx}>{t("inventoryReconciliation.colItemCode",    { defaultValue: "Item Code" })}</TableCell>
                    <TableCell sx={thSx}>{t("inventoryReconciliation.colDescription", { defaultValue: "Description" })}</TableCell>
                    <TableCell sx={{ ...thSx, textAlign: "right" }}>{t("inventoryReconciliation.colB2BTotal",   { defaultValue: "B2B Total Qty" })}</TableCell>
                    <TableCell sx={{ ...thSx, textAlign: "right" }}>{t("inventoryReconciliation.colB2BPending", { defaultValue: "B2B Pending Qty" })}</TableCell>
                    <TableCell sx={{ ...thSx, textAlign: "right" }}>{t("inventoryReconciliation.colSageStock",  { defaultValue: "Sage X3 Stock" })}</TableCell>
                    <TableCell sx={{ ...thSx, textAlign: "right" }}>{t("inventoryReconciliation.colGap",        { defaultValue: "Gap" })}</TableCell>
                    <TableCell sx={thSx}>{t("inventoryReconciliation.colStockSite",   { defaultValue: "Stock Site" })}</TableCell>
                    <TableCell sx={thSx}>{t("inventoryReconciliation.colStatus",      { defaultValue: "Status" })}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredRows.map((row, idx) => {
                    const s = STATUS_STYLES[row.status];
                    const gapColor = row.gap === 0 ? NJIM.success : row.status === "pending" ? NJIM.warning : NJIM.danger;
                    return (
                      <TableRow
                        key={row.id}
                        sx={{ background: ROW_BG[row.status], "&:hover": { filter: "brightness(0.97)" }, transition: "filter 0.12s" }}
                      >
                        <TableCell sx={{ ...tdSx, textAlign: "center", color: NJIM.textMuted, fontSize: 12, fontWeight: 600 }}>{idx + 1}</TableCell>

                        <TableCell sx={{ ...tdSx, fontFamily: "monospace", fontWeight: 700, color: BRAND, fontSize: 12 }}>
                          {row.itemCode}
                        </TableCell>

                        <TableCell sx={{ ...tdSx, maxWidth: 220 }}>
                          <Typography sx={{ fontSize: 13, color: NJIM.textPrimary, fontWeight: 500 }}>{row.description}</Typography>
                        </TableCell>

                        <TableCell sx={{ ...tdSx, textAlign: "right", fontWeight: 700, color: NJIM.textPrimary }}>
                          {row.b2bTotalQty.toLocaleString("en-US")}
                        </TableCell>

                        <TableCell sx={{ ...tdSx, textAlign: "right" }}>
                          {row.b2bPendingQty > 0 ? (
                            <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, color: NJIM.warning, fontWeight: 700 }}>
                              <AccessTimeOutlinedIcon sx={{ fontSize: 13 }} />
                              {row.b2bPendingQty.toLocaleString("en-US")}
                            </Box>
                          ) : (
                            <Typography sx={{ fontSize: 13, color: NJIM.textMuted }}>—</Typography>
                          )}
                        </TableCell>

                        <TableCell sx={{ ...tdSx, textAlign: "right", fontWeight: 700, color: NJIM.textPrimary }}>
                          {row.sageStock.toLocaleString("en-US")}
                        </TableCell>

                        <TableCell sx={{ ...tdSx, textAlign: "right" }}>
                          <Typography sx={{ fontSize: 13, fontWeight: 800, color: gapColor }}>
                            {row.gap === 0 ? "0" : row.gap > 0 ? `+${row.gap}` : `${row.gap}`}
                          </Typography>
                        </TableCell>

                        <TableCell sx={tdSx}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                            <LocationOnOutlinedIcon sx={{ fontSize: 13, color: NJIM.textMuted }} />
                            <Typography sx={{ fontSize: 13, fontWeight: 600, color: NJIM.textSecondary }}>{row.stockSite}</Typography>
                          </Box>
                        </TableCell>

                        <TableCell sx={tdSx}>
                          <Box sx={{ display: "inline-flex", alignItems: "center", px: 1.2, py: 0.4, borderRadius: "6px", fontSize: 11, fontWeight: 700, background: s.bg, color: s.color }}>
                            {t(`inventoryReconciliation.status.${row.status}`, { defaultValue: s.label })}
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Box>
          )}
        </Box>

        {/* ── Info Card ── */}
        <Box sx={{ ...glassCardSx, p: 2.5, mt: 2.5, borderLeft: "4px solid rgba(10,61,120,0.5)" }}>
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
            <InfoOutlinedIcon sx={{ fontSize: 20, color: BRAND, flexShrink: 0, mt: 0.1 }} />
            <Box>
              <Typography sx={{ fontSize: 14, fontWeight: 700, color: NJIM.textPrimary, mb: 0.5 }}>
                {t("inventoryReconciliation.infoTitle", { defaultValue: "Understanding B2B Pending Qty" })}
              </Typography>
              <Typography sx={{ fontSize: 13, color: NJIM.textSecondary, lineHeight: 1.7 }}>
                {t("inventoryReconciliation.infoBody1", { defaultValue: "\"B2B Pending Qty\" represents quantities that have been allocated to confirmed sales orders in the B2B platform but have not yet been posted (picked, packed, or shipped) to Sage X3. This creates a temporary gap between the two systems." })}
              </Typography>
              <Box sx={{ mt: 1.5, display: "flex", flexWrap: "wrap", gap: 2 }}>
                {[
                  { icon: <CheckCircleOutlineIcon sx={{ fontSize: 14, color: NJIM.success }} />, text: t("inventoryReconciliation.infoNormal",   { defaultValue: "Pending differences are expected — they reflect in-progress orders." }), color: NJIM.success },
                  { icon: <WarningAmberOutlinedIcon sx={{ fontSize: 14, color: NJIM.danger }} />, text: t("inventoryReconciliation.infoConcern", { defaultValue: "Discrepancy rows (gap ≠ pending qty) need manual investigation." }), color: NJIM.danger },
                ].map((item, i) => (
                  <Box key={i} sx={{ display: "flex", alignItems: "flex-start", gap: 0.8, flex: "1 1 300px" }}>
                    <Box sx={{ mt: 0.2 }}>{item.icon}</Box>
                    <Typography sx={{ fontSize: 12, color: item.color, fontWeight: 500 }}>{item.text}</Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>
        </Box>

      </Box>
    </Box>
  );
};

export default InventoryReconciliation;
